/**
 * Public categories listing page.
 * 
 * Features:
 * - Grid of all categories with essay counts
 * - Links to category pages
 * 
 * Called by:
 * - App.tsx as /categories route
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Typography, Spin, Alert, Card, Tag, Row, Col } from 'antd';
import { FolderOutlined } from '@ant-design/icons';
import { getPublicCategories } from '../../services/api';

const { Title, Text, Paragraph } = Typography;

/**
 * Public categories page component.
 */
function PublicCategoriesPage() {
  const { data: categories, isLoading, error } = useQuery({
    queryKey: ['public', 'categories'],
    queryFn: getPublicCategories,
  });

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        message="Error loading categories"
        description="Unable to load categories. Please try again later."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '48px' }}>
        <Title
          style={{
            fontSize: '2.5rem',
            fontFamily: "var(--font-serif)",
            fontWeight: 400,
            color: '#f5f5f5',
            marginBottom: '16px',
          }}
        >
          Categories
        </Title>
        <Paragraph style={{ color: '#a0a0a0', fontSize: '1.1rem' }}>
          Browse essays organized by topic.
        </Paragraph>
      </div>

      {/* Empty state */}
      {categories && categories.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text type="secondary">No categories yet.</Text>
        </div>
      )}

      {/* Categories grid */}
      {categories && categories.length > 0 && (
        <Row gutter={[24, 24]}>
          {categories.map((category) => (
            <Col key={category.id} xs={24} sm={12} md={8} lg={6}>
              <Link to={`/category/${category.slug}`} style={{ textDecoration: 'none' }}>
                <Card
                  hoverable
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    height: '100%',
                    transition: 'all 0.3s ease',
                  }}
                  bodyStyle={{ padding: '24px' }}
                  className="category-card"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <FolderOutlined style={{ fontSize: '1.5rem', color: '#d4af37' }} />
                    <Title
                      level={4}
                      style={{
                        color: '#f5f5f5',
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                      }}
                    >
                      {category.name}
                    </Title>
                  </div>
                  
                  {category.description && (
                    <Paragraph
                      ellipsis={{ rows: 2 }}
                      style={{
                        color: '#a0a0a0',
                        fontSize: '0.9rem',
                        marginBottom: '12px',
                      }}
                    >
                      {category.description}
                    </Paragraph>
                  )}

                  <Tag
                    style={{
                      background: 'rgba(212, 175, 55, 0.15)',
                      border: 'none',
                      color: '#d4af37',
                    }}
                  >
                    {category.essayCount} {category.essayCount === 1 ? 'essay' : 'essays'}
                  </Tag>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      )}

      <style>{`
        .category-card:hover {
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateY(-4px);
        }
      `}</style>
    </div>
  );
}

export default PublicCategoriesPage;
